import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, FileText, Printer, Save, Search, Sparkles } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { EmptyState, ErrorState } from '../../components/ui/EmptyState';
import { useConfirm } from '../../components/ui/confirm';
import { useToast } from '../../components/ui/toast';
import { useAuthStore } from '../../lib/store';
import { useDraftJdWithAi, useJd, useJds, useSaveJd, type JdDraft } from '../../hooks';

const EMPTY_DRAFT: JdDraft = { summary: '', duties: '', qualifications: '', kpis: '' };
const FIELD_LABELS: Array<{ key: keyof JdDraft; label: string; placeholder: string }> = [
  { key: 'summary', label: 'สรุปงาน', placeholder: 'อธิบายวัตถุประสงค์และขอบเขตของตำแหน่ง' },
  { key: 'duties', label: 'หน้าที่และความรับผิดชอบ', placeholder: 'ระบุหน้าที่หลักเป็นข้อ ๆ' },
  { key: 'qualifications', label: 'คุณสมบัติ', placeholder: 'ระบุความรู้ ทักษะ ประสบการณ์ หรือใบอนุญาตที่จำเป็น' },
  { key: 'kpis', label: 'ตัวชี้วัดผลงาน', placeholder: 'ระบุผลลัพธ์ที่ใช้ติดตามผลงาน โดยเว้นตัวเลขที่ยังไม่ได้กำหนด' },
];

function apiError(error: unknown): { code: string; message: string } {
  if (error instanceof Error) {
    return { code: String((error as Error & { code?: string }).code ?? ''), message: error.message };
  }
  return { code: '', message: 'ร่างด้วย AI ไม่สำเร็จ' };
}

function AiError({ error }: { error: { code: string; message: string } | null }) {
  if (!error) return null;
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
      {error.message}
      {error.code === 'AI_NOT_CONFIGURED' && (
        <Link to="/owner/settings#ai" className="ml-1 font-medium underline">ไปที่ตั้งค่า AI</Link>
      )}
    </div>
  );
}

export function JobDescriptions() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const { data = [], isLoading, isError, error, refetch } = useJds();
  const filtered = useMemo(() => {
    const q = search.trim().toLocaleLowerCase('th');
    return q ? data.filter((item) => item.title.toLocaleLowerCase('th').includes(q)) : data;
  }, [data, search]);

  return (
    <AdminLayout title="คำบรรยายลักษณะงาน (JD)">
      <div className="mx-auto max-w-3xl space-y-4 pb-10">
        <p className="text-sm text-gray-600">จัดทำ JD ตามชื่อตำแหน่งที่ใช้จริงในทะเบียนพนักงาน และตรวจแก้ทุกฉบับก่อนบันทึก</p>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            className="input w-full pl-9"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="ค้นหาตำแหน่ง"
          />
        </div>

        {isLoading && <div className="card p-8 text-center text-sm text-gray-500">กำลังโหลด…</div>}
        {isError && <div className="card"><ErrorState detail={error instanceof Error ? error.message : undefined} onRetry={() => void refetch()} /></div>}
        {!isLoading && !isError && filtered.length === 0 && (
          <div className="card">
            <EmptyState
              icon={FileText}
              title={search ? 'ไม่พบตำแหน่งที่ค้นหา' : 'ยังไม่มีตำแหน่งพนักงาน'}
              detail={search ? 'ลองค้นหาด้วยคำอื่น' : 'เพิ่มตำแหน่งในทะเบียนพนักงานก่อน แล้วตำแหน่งจะปรากฏที่นี่'}
            />
          </div>
        )}

        <div className="space-y-2">
          {filtered.map((item) => (
            <button
              key={item.title}
              type="button"
              onClick={() => navigate(`/hr/jds/${encodeURIComponent(item.title)}`)}
              className="card flex w-full items-center gap-3 p-4 text-left transition hover:border-gray-300"
            >
              <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gray-100 text-gray-600">
                <FileText className="h-5 w-5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium text-gray-900">{item.title}</span>
                <span className="text-xs text-gray-500">พนักงาน {item.employee_count} คน</span>
              </span>
              <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${item.has_jd ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                {item.has_jd ? 'มีแล้ว' : 'ยังไม่มี'}
              </span>
            </button>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}

export function JobDescriptionEdit() {
  const { title: routeTitle } = useParams<{ title: string }>();
  const title = routeTitle?.trim();
  const profile = useAuthStore((state) => state.profile);
  const confirm = useConfirm();
  const toast = useToast();
  const { data, isLoading, isError, error, refetch } = useJd(title);
  const save = useSaveJd(title);
  const draftWithAi = useDraftJdWithAi(title);
  const [form, setForm] = useState<JdDraft>(EMPTY_DRAFT);
  const [notes, setNotes] = useState('');
  const [source, setSource] = useState<'manual' | 'ai_edited'>('manual');
  const [aiErrorState, setAiErrorState] = useState<{ code: string; message: string } | null>(null);

  useEffect(() => {
    if (!data) return;
    setForm({
      summary: data.summary ?? '',
      duties: data.duties ?? '',
      qualifications: data.qualifications ?? '',
      kpis: data.kpis ?? '',
    });
    setSource(data.source ?? 'manual');
  }, [data]);

  async function draftAi() {
    if (Object.values(form).some((value) => value.trim())) {
      const ok = await confirm({
        title: 'แทนที่เนื้อหาเดิมด้วยฉบับร่างจาก AI?',
        message: 'เนื้อหาในช่องทั้ง 4 ส่วนจะถูกแทนที่ แต่ยังไม่บันทึกจนกว่าจะกดบันทึก',
        confirmText: 'ร่างใหม่',
      });
      if (!ok) return;
    }
    setAiErrorState(null);
    try {
      const result = await draftWithAi.mutateAsync({ notes: notes.trim() || undefined });
      setForm(result);
      setSource('ai_edited');
      toast.success('สร้างฉบับร่างแล้ว ตรวจแก้ก่อนบันทึก');
    } catch (err) {
      setAiErrorState(apiError(err));
    }
  }

  async function saveJd() {
    try {
      await save.mutateAsync({ ...form, source });
      toast.success('บันทึกคำบรรยายลักษณะงานแล้ว');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'บันทึกไม่สำเร็จ');
    }
  }

  if (!title) return <AdminLayout title="คำบรรยายลักษณะงาน (JD)"><ErrorState title="ชื่อตำแหน่งไม่ถูกต้อง" /></AdminLayout>;

  return (
    <AdminLayout title="คำบรรยายลักษณะงาน (JD)">
      <style>{`
        #jd-print-sheet { display: none; }
        @media print {
          body * { visibility: hidden !important; }
          #jd-print-sheet, #jd-print-sheet * { visibility: visible !important; }
          #jd-print-sheet { display: block !important; position: absolute; inset: 0; width: 100%; color: #111; background: #fff; }
          @page { size: A4; margin: 18mm; }
        }
      `}</style>
      <div className="mx-auto max-w-3xl space-y-4 pb-12">
        <Link to="/hr/jds" className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900">
          <ArrowLeft className="h-4 w-4" /> กลับไปรายการตำแหน่ง
        </Link>
        <div className="card p-5">
          <p className="text-xs text-gray-500">ตำแหน่ง</p>
          <h2 className="mt-1 text-xl font-semibold text-gray-900">{title}</h2>
        </div>

        {isLoading && <div className="card p-8 text-center text-sm text-gray-500">กำลังโหลด…</div>}
        {isError && <div className="card"><ErrorState detail={error instanceof Error ? error.message : undefined} onRetry={() => void refetch()} /></div>}
        {!isLoading && !isError && (
          <>
            <div className="card space-y-3 p-5">
              <div>
                <label className="label">ข้อมูลเพิ่มเติมสำหรับ AI (ไม่บังคับ)</label>
                <input
                  className="input w-full"
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  maxLength={2000}
                  placeholder="เช่น ดูแลสาขากลางคืน เน้นงานบริการลูกค้า"
                />
              </div>
              <button type="button" onClick={() => void draftAi()} disabled={draftWithAi.isPending} className="btn-secondary inline-flex items-center gap-2">
                <Sparkles className="h-4 w-4" /> {draftWithAi.isPending ? 'กำลังร่าง…' : 'ร่างด้วย AI'}
              </button>
              <p className="text-xs text-gray-500">AI สร้างเฉพาะฉบับร่าง ยังไม่บันทึกหรือเผยแพร่</p>
              <AiError error={aiErrorState} />
            </div>

            <div className="space-y-4">
              {FIELD_LABELS.map((field) => (
                <div key={field.key} className="card p-5">
                  <label className="label">{field.label}</label>
                  <textarea
                    className="input min-h-[150px] w-full"
                    value={form[field.key]}
                    onChange={(event) => setForm((current) => ({ ...current, [field.key]: event.target.value }))}
                    maxLength={4000}
                    placeholder={field.placeholder}
                  />
                  <p className="mt-1 text-right text-xs text-gray-400">{form[field.key].length}/4000</p>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap justify-end gap-2">
              <button type="button" onClick={() => window.print()} className="btn-secondary inline-flex items-center gap-2">
                <Printer className="h-4 w-4" /> พิมพ์
              </button>
              <button type="button" onClick={() => void saveJd()} disabled={save.isPending} className="btn-primary inline-flex items-center gap-2">
                <Save className="h-4 w-4" /> {save.isPending ? 'กำลังบันทึก…' : 'บันทึก'}
              </button>
            </div>
          </>
        )}
      </div>

      <section id="jd-print-sheet" aria-hidden="true">
        <div style={{ textAlign: 'center', borderBottom: '1px solid #999', paddingBottom: 16, marginBottom: 24 }}>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{profile?.tenant_name ?? 'บริษัท'}</div>
          <div style={{ fontSize: 22, fontWeight: 700, marginTop: 8 }}>คำบรรยายลักษณะงาน (JD)</div>
          <div style={{ fontSize: 16, marginTop: 6 }}>ตำแหน่ง {title}</div>
        </div>
        {FIELD_LABELS.map((field, index) => (
          <div key={field.key} style={{ marginBottom: 20, breakInside: 'avoid' }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 6 }}>{index + 1}. {field.label}</h3>
            <div style={{ whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.7 }}>{form[field.key] || '—'}</div>
          </div>
        ))}
      </section>
    </AdminLayout>
  );
}
